import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, signal, Component, effect, inject, input, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { VistoTemporarioSolicitacaoService } from '@/core/api/sicom-api/registro/medico/visto-temporario/visto-temporario-solicitacao.service';
import type { VistoTemporarioSolicitacaoAtualizacaoEnderecoRequest, VistoTemporarioSolicitacaoAtualizacaoPeriodoEEnderecosRequest } from '@/core/api/sicom-api/registro/medico/visto-temporario/visto-temporario-solicitacao.model';
import { LocalidadesService } from '@/core/api/consulta-api/localidades/localidades.service';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { InputMask } from 'primeng/inputmask';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Textarea } from 'primeng/textarea';
import { filter, map } from 'rxjs';
import type { NovoVistoSolicitacao } from '../novo-visto/novo-visto';
import type { VistoTemporarioProtocoloSelecionado } from '../visto-temporario.types';
import { CardContent } from '@/shared/components/card-content/card-content';
import { PessoaAvatarComponent } from '@/shared/components/pessoa/pessoa-avatar/pessoa-avatar';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
interface MedicoRelacionadoRow {
    uf: string;
    medico: string;
    cpf: string;
}

interface OutroVistoRow {
    apto: string;
    dataInicio: string;
    dataFim: string;
    dias: string;
}

/** Severidade do selo de situação, na escala de `_tabela-tons.scss` — mesmo padrão do modal de busca do relatório por seleção. */
type SituacaoBadgeSeveridade = 'success' | 'danger' | 'secondary';

@Component({
    selector: 'app-gerenciar-visto',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, CommonModule, ReactiveFormsModule, InputMask, InputText, TableModule, Textarea, BotaoAcoes, Dialog, Button, CardContent, CanDirective, PessoaAvatarComponent],
    templateUrl: './gerenciar-visto.html',
    styleUrl: './gerenciar-visto.scss'
})
/** Gerenciamento de solicitação de visto temporário. */
export class GerenciarVistoComponent {
    protected readonly EInconPos = EInconPos;
    protected readonly roleVistoEditar = PERMISSOES.medicos.vistoTemporario.editar;
    pessoa = input<Pessoa>();
    acaoSelecionada = input<'LOCALIZAR' | 'NOVO'>('LOCALIZAR');
    dadosNovoVisto = input<NovoVistoSolicitacao | null>(null);
    dadosLocalizarVisto = input<VistoTemporarioProtocoloSelecionado | null>(null);
    voltar = output<void>();
    private readonly fb = inject(FormBuilder);

    private readonly localidadesService = inject(LocalidadesService);
    private readonly vistoTemporarioSolicitacaoService = inject(VistoTemporarioSolicitacaoService);
    private readonly messageService = inject(MessageService);

    vistoTemporarioForm = this.fb.group({
        protocoloAno: [''],
        protocoloNumero: [''],
        tipo: [''],
        documentoOrigem: [''],
        solicitante: [''],
        solicitanteCpfCnpj: [''],
        requerente: [''],
        requerenteCpfCnpj: [''],
        ufOrigem: [''],
        crmOrigem: [''],
        finalidade: [''],
        periodoInicio: [''],
        periodoFim: [''],
        contatoCep: [''],
        contatoEndereco: [''],
        contatoBairro: [''],
        contatoMunicipio: [''],
        contatoDdd: [''],
        contatoTelefone: [''],
        trabalhoCep: [''],
        trabalhoEndereco: [''],
        trabalhoBairro: [''],
        trabalhoMunicipio: [''],
        trabalhoDdd: [''],
        trabalhoTelefone: ['']
    });

    medicosRelacionados = signal<MedicoRelacionadoRow[]>([]);
    outrosVistosCadastrados = signal<OutroVistoRow[]>([]);

    statusSolicitacao = 'Pendente';
    private situacaoSolicitacaoCodigo: 'A' | 'R' | 'P' = 'P';
    editando = false;
    private salvandoSolicitacao = signal(false);
    private contatoIdMunicipio?: number;
    private trabalhoIdMunicipio?: number;
    private formSnapshot: ReturnType<GerenciarVistoComponent['vistoTemporarioForm']['getRawValue']> | null = null;
    dialogConfirmacaoVisivel = false;
    tituloDialogConfirmacao = '';
    mensagemDialogConfirmacao = '';
    private acaoPendenteConfirmacao: 'APROVAR' | 'REPROVAR' | 'FINALIZAR' | null = null;

    /** Foto ampliada do solicitante clicado — mesmo padrão de zoom da Capa de PAT/Dívida Ativa. */
    fotoAmpliada = signal<string | null>(null);
    fotoAmpliadaVisivel = signal(false);

    constructor() {
        this.definirModoEdicao(false);

        effect(() => {
            const dados = this.dadosNovoVisto();

            if (!dados) {
                return;
            }

            this.atualizarStatusSolicitacao();
            this.contatoIdMunicipio = undefined;
            this.trabalhoIdMunicipio = undefined;

            this.vistoTemporarioForm.patchValue({
                tipo: dados.tipoVisto === '90_CORRIDOS' ? '90 dias corridos' : '90 dias fracionados',
                solicitante: dados.solicitante,
                requerente: dados.requerente,
                finalidade: dados.finalidade,
                periodoInicio: dados.periodoInicio,
                periodoFim: dados.periodoFim,
                ufOrigem: dados.ufOrigem,
                crmOrigem: dados.crmOrigem
            });
            this.medicosRelacionados.set([]);
            this.outrosVistosCadastrados.set([]);
            this.definirModoEdicao(false);
        });

        effect(() => {
            const dados = this.dadosLocalizarVisto();

            if (!dados) {
                return;
            }

            this.atualizarStatusSolicitacao(dados.inSituacao);
            this.contatoIdMunicipio = dados.contatoIdMunicipio;
            this.trabalhoIdMunicipio = dados.trabalhoIdMunicipio;
            const telefoneContatoNormalizado = this.normalizarDddTelefone(dados.contatoDdd, dados.contatoTelefone);
            const telefoneTrabalhoNormalizado = this.normalizarDddTelefone(dados.trabalhoDdd, dados.trabalhoTelefone);

            this.vistoTemporarioForm.patchValue({
                protocoloNumero: dados.numeroProtocolo,
                protocoloAno: dados.anoProtocolo,
                tipo: dados.tipoVisto ?? '',
                documentoOrigem: this.montarDocumentoOrigem(dados),
                solicitante: dados.solicitante ?? '',
                solicitanteCpfCnpj: dados.solicitanteCpfCnpj ?? '',
                requerente: dados.requerente ?? '',
                requerenteCpfCnpj: dados.requerenteCpfCnpj ?? '',
                ufOrigem: dados.ufOrigem ?? '',
                crmOrigem: dados.crmOrigem ?? '',
                finalidade: dados.finalidade ?? '',
                periodoInicio: dados.periodoInicio ?? '',
                periodoFim: dados.periodoFim ?? '',
                contatoCep: dados.contatoCep ?? '',
                contatoEndereco: dados.contatoEndereco ?? '',
                contatoBairro: dados.contatoBairro ?? '',
                contatoMunicipio: dados.contatoMunicipio ?? '',
                contatoDdd: telefoneContatoNormalizado.ddd,
                contatoTelefone: telefoneContatoNormalizado.telefone,
                trabalhoCep: dados.trabalhoCep ?? '',
                trabalhoEndereco: dados.trabalhoEndereco ?? '',
                trabalhoBairro: dados.trabalhoBairro ?? '',
                trabalhoMunicipio: dados.trabalhoMunicipio ?? '',
                trabalhoDdd: telefoneTrabalhoNormalizado.ddd,
                trabalhoTelefone: telefoneTrabalhoNormalizado.telefone
            });

            this.medicosRelacionados.set(
                (dados.medicosRelacionados ?? []).map((item) => ({
                    uf: item.uf,
                    medico: item.medico,
                    cpf: item.cpf
                }))
            );

            this.outrosVistosCadastrados.set(
                (dados.outrosVistosCadastrados ?? []).map((item) => ({
                    apto: item.apto,
                    dataInicio: item.dataInicio,
                    dataFim: item.dataFim,
                    dias: item.dias
                }))
            );
            this.definirModoEdicao(false);
        });

        this.observarCepParaAutopreenchimento('contato');
        this.observarCepParaAutopreenchimento('trabalho');
    }

    onVoltar(): void {
        this.voltar.emit();
    }

    get protocoloAnoDisplay(): string {
        const protocolo = this.formatarValor(this.vistoTemporarioForm.get('protocoloNumero')?.value);
        const ano = this.formatarValor(this.vistoTemporarioForm.get('protocoloAno')?.value);

        if (protocolo !== '-' && ano !== '-') {
            return `${protocolo}/${ano}`;
        }

        return protocolo !== '-' ? protocolo : ano;
    }

    get tipoVistoDisplay(): string {
        return this.formatarValor(this.vistoTemporarioForm.get('tipo')?.value);
    }

    get solicitanteDisplay(): string {
        return this.formatarValor(this.vistoTemporarioForm.get('solicitante')?.value);
    }

    get isVisto90Fracionado(): boolean {
        const tipo = (this.vistoTemporarioForm.get('tipo')?.value ?? '').toString().trim();
        if (!tipo) {
            return false;
        }

        const tipoNormalizado = tipo
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase();
        return tipoNormalizado.includes('FRACION');
    }

    /** Classe do selo de situação — reaproveita a escala de tons de `_protocolo-card.scss` (verde/âmbar/vermelho), mesmo padrão do card de protocolo dos Serviços. */
    get classeSituacaoTag(): string {
        switch (this.situacaoSolicitacaoCodigo) {
            case 'A':
                return 'situacao-ativo';
            case 'R':
                return 'situacao-cancelado';
            default:
                return 'situacao-pendencia';
        }
    }

    /** Identidade do `app-pessoa-avatar`: prioriza o protocolo localizado; sem ele, cai na pessoa do dashboard (fluxo de Novo Visto). */
    get idSolicitanteAvatar(): number | null {
        return this.dadosLocalizarVisto()?.idSolicitante ?? this.pessoa()?.id ?? null;
    }

    get ufAvatar(): string {
        return (this.dadosLocalizarVisto()?.ufSgUf ?? this.pessoa()?.uf ?? '').trim();
    }

    /** Linha "CRM/UF de origem" sob o nome do solicitante, mesmo papel do `protocolo-card-documento`. */
    get crmUfOrigemDisplay(): string {
        const crm = this.formatarValor(this.vistoTemporarioForm.get('crmOrigem')?.value);
        const uf = this.formatarValor(this.vistoTemporarioForm.get('ufOrigem')?.value);

        if (crm === '-') {
            return uf;
        }

        return uf === '-' ? `CRM ${crm}` : `CRM ${crm}/${uf}`;
    }

    get itensAcoesVisto(): AppMenuItem[] {
        const role = this.roleVistoEditar;
        // Toda ação do menu age sobre a solicitação inteira: durante a edição elas descartariam
        // em silêncio o que ainda não foi gravado.
        const editando = this.editando;
        return [
            acaoMenuItem({ id: 'editar', label: 'Editar', icon: 'pi pi-pencil', cor: 'editar', disabled: editando, permission: role, command: () => this.definirModoEdicao(true) }),
            // Item sem efeito: nunca houve ação de exclusão ligada a ele, e este refactor
            // preservou esse comportamento de propósito. Corrigir ou remover é assunto de
            // outro chamado — no mobile ele ainda pede "Deseja deletar?" e não faz nada.
            acaoMenuItem({ id: 'deletar', label: 'Deletar', icon: 'pi pi-trash', cor: 'remover', disabled: editando, permission: role, command: () => {} }),
            acaoMenuItem({ id: 'finalizar', label: 'Finalizar Solicitação de Visto', icon: 'pi pi-check', cor: 'neutro', disabled: editando, permission: role, command: () => this.abrirDialogConfirmacaoAcao('FINALIZAR') }),
            acaoMenuItem({ id: 'aprovar', label: 'Aprovar Solicitação', icon: 'pi pi-check-circle', cor: 'novo', disabled: editando, permission: role, command: () => this.abrirDialogConfirmacaoAcao('APROVAR') }),
            acaoMenuItem({ id: 'reprovar', label: 'Reprovar Solicitação', icon: 'pi pi-times-circle', cor: 'cancelar', disabled: editando, permission: role, command: () => this.abrirDialogConfirmacaoAcao('REPROVAR') })
        ];
    }

    /** Ver itensAcoesVisto: Salvar e Cancelar saem do menu e viram ação em destaque. */
    get itensEdicao(): AppMenuItem[] {
        const role = this.roleVistoEditar;
        return [
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', permission: role, command: () => this.onSalvarEdicao() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.onCancelarEdicao() })
        ];
    }

    ampliarFoto(foto: string): void {
        this.fotoAmpliada.set(foto);
        this.fotoAmpliadaVisivel.set(true);
    }

    /** Severidade do selo de "Outros Vistos Cadastrados" \u2014 mesmo padr\u00e3o de selo do modal de busca do relat\u00f3rio por sele\u00e7\u00e3o (`_tabela-tons.scss`). */
    situacaoOutroVistoSeveridade(situacao: string): SituacaoBadgeSeveridade {
        const valorNormalizado = (situacao ?? '')
            .trim()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase();

        if (valorNormalizado === 'APTO') {
            return 'success';
        }

        if (valorNormalizado === 'RECUSADO') {
            return 'danger';
        }

        return 'secondary';
    }

    private formatarValor(valor: unknown): string {
        const texto = (valor ?? '').toString().trim();
        return texto || '-';
    }

    private montarDocumentoOrigem(dados: VistoTemporarioProtocoloSelecionado): string {
        const numeroDocumentoOrigem = (dados.numeroDocumentoOrigem ?? '').trim();
        const origem = (dados.inOrigem ?? '').trim();

        if (numeroDocumentoOrigem && origem) {
            return `${origem} - ${numeroDocumentoOrigem}`;
        }

        return numeroDocumentoOrigem || origem;
    }

    private atualizarStatusSolicitacao(inSituacao?: string): void {
        const situacao = (inSituacao ?? '').trim().toUpperCase();

        if (situacao === 'A') {
            this.situacaoSolicitacaoCodigo = 'A';
            this.statusSolicitacao = 'Aprovado';
            return;
        }

        if (situacao === 'R') {
            this.situacaoSolicitacaoCodigo = 'R';
            this.statusSolicitacao = 'Reprovado';
            return;
        }

        this.situacaoSolicitacaoCodigo = 'P';
        this.statusSolicitacao = 'Pendente';
    }

    private enviarAtualizacaoPeriodoEEnderecos(inSituacao: 'A' | 'R', finalizarEdicao = false): void {
        if (this.salvandoSolicitacao()) {
            return;
        }

        const payload = this.montarPayloadAtualizacao(inSituacao);
        if (!payload) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Não foi possível enviar a atualização: faltam dados obrigatórios.'
            });
            return;
        }

        this.salvandoSolicitacao.set(true);
        this.vistoTemporarioSolicitacaoService.atualizarPeriodoEEnderecos(payload).subscribe({
            next: () => {
                this.atualizarStatusSolicitacao(inSituacao);
                if (finalizarEdicao) {
                    this.definirModoEdicao(false);
                }
                this.messageService.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Solicitação atualizada com sucesso.'
                });
            },
            // Sem toast local: o componente reimplementava a extração da mensagem que o
            // UiErrorMessageBuilder já faz no interceptor global — o resultado era um segundo toast
            // com o mesmo texto. O helper foi removido junto por ficar sem uso.
            error: () => {},
            complete: () => {
                this.salvandoSolicitacao.set(false);
            }
        });
    }

    private montarPayloadAtualizacao(inSituacao: 'A' | 'R'): VistoTemporarioSolicitacaoAtualizacaoPeriodoEEnderecosRequest | null {
        const dadosProtocolo = this.dadosLocalizarVisto();
        const idProtocolo = dadosProtocolo?.idProtocolo;
        const idSolicitante = dadosProtocolo?.idSolicitante ?? this.pessoa()?.id;
        const ufSgUf = (dadosProtocolo?.ufSgUf ?? this.pessoa()?.uf ?? '').trim().toUpperCase();

        const dadosFormulario = this.vistoTemporarioForm.getRawValue();
        const dataInicioPeriodo = this.converterDataParaIso(dadosFormulario.periodoInicio);
        const dataFinalPeriodo = this.converterDataParaIso(dadosFormulario.periodoFim);

        const enderecoContato = {
            idMunicipio: this.contatoIdMunicipio,
            txEndereco: (dadosFormulario.contatoEndereco ?? '').toString().trim(),
            nmBairro: (dadosFormulario.contatoBairro ?? '').toString().trim(),
            nuCep: this.manterSomenteDigitos(dadosFormulario.contatoCep),
            nuCodigoDdd: this.manterSomenteDigitos(dadosFormulario.contatoDdd),
            nuTelefone: this.manterSomenteDigitos(dadosFormulario.contatoTelefone)
        };

        const enderecoTrabalho = {
            idMunicipio: this.trabalhoIdMunicipio,
            txEndereco: (dadosFormulario.trabalhoEndereco ?? '').toString().trim(),
            nmBairro: (dadosFormulario.trabalhoBairro ?? '').toString().trim(),
            nuCep: this.manterSomenteDigitos(dadosFormulario.trabalhoCep),
            nuCodigoDdd: this.manterSomenteDigitos(dadosFormulario.trabalhoDdd),
            nuTelefone: this.manterSomenteDigitos(dadosFormulario.trabalhoTelefone)
        };

        const payloadInvalido = typeof idProtocolo !== 'number' || typeof idSolicitante !== 'number' || !ufSgUf || !dataInicioPeriodo || !dataFinalPeriodo;

        if (payloadInvalido) {
            return null;
        }

        const payload: VistoTemporarioSolicitacaoAtualizacaoPeriodoEEnderecosRequest = {
            idProtocolo,
            idSolicitante,
            ufSgUf,
            dataInicioPeriodo,
            dataFinalPeriodo,
            inSituacao
        };

        const enderecoContatoAtualizacao = this.montarEnderecoAtualizacao(enderecoContato);
        if (enderecoContatoAtualizacao) {
            payload.enderecoContato = enderecoContatoAtualizacao;
        }

        const enderecoTrabalhoAtualizacao = this.montarEnderecoAtualizacao(enderecoTrabalho);
        if (enderecoTrabalhoAtualizacao) {
            payload.enderecoTrabalho = enderecoTrabalhoAtualizacao;
        }

        return payload;
    }

    private montarEnderecoAtualizacao(endereco: { idMunicipio?: number; txEndereco: string; nmBairro: string; nuCep: string; nuCodigoDdd: string; nuTelefone: string }): VistoTemporarioSolicitacaoAtualizacaoEnderecoRequest | undefined {
        if (typeof endereco.idMunicipio !== 'number' || !endereco.txEndereco || !endereco.nmBairro || !endereco.nuCep || !endereco.nuCodigoDdd || !endereco.nuTelefone) {
            return undefined;
        }

        return {
            idMunicipio: endereco.idMunicipio,
            txEndereco: endereco.txEndereco,
            nmBairro: endereco.nmBairro,
            nuCep: endereco.nuCep,
            nuCodigoDdd: endereco.nuCodigoDdd,
            nuTelefone: endereco.nuTelefone
        };
    }

    private obterSituacaoParaEdicao(): 'A' | 'R' | null {
        if (this.situacaoSolicitacaoCodigo === 'A' || this.situacaoSolicitacaoCodigo === 'R') {
            return this.situacaoSolicitacaoCodigo;
        }

        return null;
    }

    private definirModoEdicao(ativo: boolean): void {
        if (ativo && !this.editando) {
            this.formSnapshot = this.vistoTemporarioForm.getRawValue();
        }

        if (!ativo) {
            this.formSnapshot = null;
        }

        this.editando = ativo;

        if (ativo) {
            this.vistoTemporarioForm.enable({ emitEvent: false });
        } else {
            this.vistoTemporarioForm.disable({ emitEvent: false });
        }
    }

    private normalizarDddTelefone(ddd?: string, telefone?: string): { ddd: string; telefone: string } {
        let dddDigitos = this.manterSomenteDigitos(ddd).slice(0, 2);
        let telefoneDigitos = this.manterSomenteDigitos(telefone);

        if (!dddDigitos && telefoneDigitos.length >= 10) {
            dddDigitos = telefoneDigitos.slice(0, 2);
            telefoneDigitos = telefoneDigitos.slice(2);
        }

        if (dddDigitos && (telefoneDigitos.length === 10 || telefoneDigitos.length === 11) && telefoneDigitos.startsWith(dddDigitos)) {
            telefoneDigitos = telefoneDigitos.slice(2);
        }

        if (telefoneDigitos.length > 9) {
            telefoneDigitos = telefoneDigitos.slice(-9);
        }

        return { ddd: dddDigitos, telefone: telefoneDigitos };
    }

    private converterDataParaIso(valor: unknown): string | null {
        const data = (valor ?? '').toString().trim();
        if (!data) {
            return null;
        }

        const somenteData = data.substring(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(somenteData)) {
            return somenteData;
        }

        const correspondenciaBr = somenteData.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!correspondenciaBr) {
            return null;
        }

        const [, dia, mes, ano] = correspondenciaBr;
        return `${ano}-${mes}-${dia}`;
    }

    private manterSomenteDigitos(valor: unknown): string {
        return (valor ?? '').toString().replace(/\D+/g, '');
    }

    private observarCepParaAutopreenchimento(tipo: 'contato' | 'trabalho'): void {
        const controleCep = this.vistoTemporarioForm.get(`${tipo}Cep`);
        if (!controleCep) {
            return;
        }

        controleCep.valueChanges
            .pipe(
                map((valor) => this.manterSomenteDigitos(valor)),
                filter((cep) => cep.length === 8 && !!controleCep.dirty)
            )
            .subscribe((cep) => this.buscarEnderecoPorCep(tipo, cep));
    }

    private buscarEnderecoPorCep(tipo: 'contato' | 'trabalho', cep: string): void {
        this.localidadesService.get(cep).subscribe({
            next: (dados) => {
                if (!dados) {
                    return;
                }

                this.vistoTemporarioForm.patchValue({
                    [`${tipo}Endereco`]: (dados.logradouro ?? '').trim(),
                    [`${tipo}Bairro`]: (dados.bairro ?? '').trim(),
                    [`${tipo}Municipio`]: (dados.municipio ?? '').trim(),
                    [`${tipo}Ddd`]: this.manterSomenteDigitos(dados.ddd).slice(0, 2)
                });
            },
            error: () => {}
        });
    }

    onSalvarEdicao(): void {
        const inSituacaoEdicao = this.obterSituacaoParaEdicao();
        if (!inSituacaoEdicao) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: "A situação deve ser 'Aprovado' ou 'Reprovado' para salvar."
            });
            return;
        }

        this.enviarAtualizacaoPeriodoEEnderecos(inSituacaoEdicao, true);
    }

    onCancelarEdicao(): void {
        if (this.formSnapshot) {
            this.vistoTemporarioForm.patchValue(this.formSnapshot, { emitEvent: false });
        }

        this.definirModoEdicao(false);
    }

    onConfirmarDialogAcao(): void {
        if (!this.acaoPendenteConfirmacao) {
            this.fecharDialogConfirmacaoAcao();
            return;
        }

        if (this.acaoPendenteConfirmacao === 'APROVAR') {
            this.enviarAtualizacaoPeriodoEEnderecos('A');
            this.fecharDialogConfirmacaoAcao();
            return;
        }

        if (this.acaoPendenteConfirmacao === 'REPROVAR') {
            this.enviarAtualizacaoPeriodoEEnderecos('R');
            this.fecharDialogConfirmacaoAcao();
            return;
        }

        const inSituacaoAtual = this.obterSituacaoParaEdicao();
        if (!inSituacaoAtual) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: "A situação deve estar 'Aprovado' ou 'Reprovado' para finalizar."
            });
            this.fecharDialogConfirmacaoAcao();
            return;
        }

        this.enviarAtualizacaoPeriodoEEnderecos(inSituacaoAtual);
        this.fecharDialogConfirmacaoAcao();
    }

    onCancelarDialogAcao(): void {
        this.fecharDialogConfirmacaoAcao();
    }

    private abrirDialogConfirmacaoAcao(acao: 'APROVAR' | 'REPROVAR' | 'FINALIZAR'): void {
        this.acaoPendenteConfirmacao = acao;
        this.dialogConfirmacaoVisivel = true;

        if (acao === 'APROVAR') {
            this.tituloDialogConfirmacao = 'Confirmar Aprovação';
            this.mensagemDialogConfirmacao = 'Deseja aprovar esta solicitação?';
            return;
        }

        if (acao === 'REPROVAR') {
            this.tituloDialogConfirmacao = 'Confirmar Reprovação';
            this.mensagemDialogConfirmacao = 'Deseja reprovar esta solicitação?';
            return;
        }

        this.tituloDialogConfirmacao = 'Confirmar Finalização';
        this.mensagemDialogConfirmacao = 'Deseja finalizar esta solicitação?';
    }

    private fecharDialogConfirmacaoAcao(): void {
        this.dialogConfirmacaoVisivel = false;
        this.acaoPendenteConfirmacao = null;
        this.tituloDialogConfirmacao = '';
        this.mensagemDialogConfirmacao = '';
    }
}
